import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Download, Copy, Check, X, Share2 } from "lucide-react";
import { toast } from "sonner";

/**
 * ShareEntryDialog — reusable modal that generates a downloadable
 * Instagram-story image + a copyable public link for a contest entry.
 *
 * Props:
 *  - open, onClose
 *  - entryId (required)
 *  - contestId (required) — used to build canonical public URL
 */
export default function ShareEntryDialog({ open, onClose, entryId, contestId }) {
  const [previewLoading, setPreviewLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = `${window.location.origin}/contest/${contestId}/entry/${entryId}`;
  // Public image URL (bust cache on each open)
  const [cacheKey, setCacheKey] = useState(0);
  const shareImageUrl = `${API}/contest/entries/${entryId}/share-image?v=${cacheKey}`;

  useEffect(() => {
    if (open) {
      setPreviewLoading(true);
      setCopied(false);
      setCacheKey((k) => k + 1);
    }
  }, [open]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Lien copié !");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const ta = document.createElement("textarea");
      ta.value = shareUrl;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); toast.success("Lien copié !"); }
      catch { toast.error("Impossible de copier"); }
      document.body.removeChild(ta);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const resp = await api.get(`/contest/entries/${entryId}/share-image`, {
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "image/jpeg" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `helencia-vote-${entryId.slice(0, 8)}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Image téléchargée !");
    } catch (err) {
      toast.error("Téléchargement impossible");
    } finally {
      setDownloading(false);
    }
  };

  // Try native Web Share if available (mobile)
  const handleNativeShare = async () => {
    if (!navigator.share) return;
    try {
      await navigator.share({
        title: "Vote pour moi sur Helencia",
        text: "Vote pour ma participation au concours sur Helencia !",
        url: shareUrl,
      });
    } catch {
      // user cancelled — ignore
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto p-0" data-testid="share-entry-dialog">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="font-serif text-2xl text-[#1A1A1A]">
            Partage ta participation
          </DialogTitle>
          <p className="text-sm text-[#6E4B2A] leading-relaxed mt-2">
            Partage cette image en story Instagram et ajoute le lien pour recevoir des votes.
          </p>
        </DialogHeader>

        <div className="px-6 pb-6">
          {/* Image preview */}
          <div className="relative rounded-2xl overflow-hidden bg-[#FAF3EA] border border-[#EADDC9] aspect-[9/16] max-h-[55vh] mx-auto">
            {previewLoading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-[#C68E59] border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            <img
              src={shareImageUrl}
              alt="Aperçu story Instagram"
              onLoad={() => setPreviewLoading(false)}
              onError={() => { setPreviewLoading(false); toast.error("Erreur chargement aperçu"); }}
              className={`w-full h-full object-contain transition ${previewLoading ? "opacity-0" : "opacity-100"}`}
              data-testid="share-preview-image"
            />
          </div>

          {/* Actions */}
          <div className="grid grid-cols-2 gap-3 mt-5">
            <Button
              onClick={handleDownload}
              disabled={downloading}
              className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white h-11"
              data-testid="share-download-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              {downloading ? "Téléchargement…" : "Télécharger"}
            </Button>
            <Button
              onClick={handleCopy}
              variant="outline"
              className="rounded-full border-[#EADDC9] text-[#1A1A1A] h-11"
              data-testid="share-copy-link-btn"
            >
              {copied ? (
                <><Check className="w-4 h-4 mr-2 text-[#2E7D32]" /> Copié</>
              ) : (
                <><Copy className="w-4 h-4 mr-2" /> Copier le lien</>
              )}
            </Button>
          </div>

          {/* Link preview */}
          <div className="mt-3 rounded-xl bg-[#FFFBF5] border border-[#EADDC9] px-3 py-2 text-[11px] text-[#6E4B2A] truncate" data-testid="share-link-preview">
            {shareUrl}
          </div>

          {/* Native share on mobile */}
          {typeof navigator !== "undefined" && navigator.share && (
            <Button
              onClick={handleNativeShare}
              variant="ghost"
              className="w-full mt-3 rounded-full text-[#A26F3E] hover:bg-[#FAF3EA] h-10"
              data-testid="share-native-btn"
            >
              <Share2 className="w-4 h-4 mr-2" /> Partager via l'application
            </Button>
          )}

          {/* Tips */}
          <div className="mt-5 rounded-2xl bg-[#FFFBF5] border border-[#EADDC9] p-4">
            <p className="caption-label text-[#A26F3E] mb-2">Comment partager</p>
            <ol className="text-xs text-[#4A4A4A] leading-relaxed space-y-1.5 list-decimal list-inside">
              <li>Télécharge l'image ci-dessus</li>
              <li>Ouvre Instagram Stories et importe l'image</li>
              <li>Ajoute le lien avec le sticker "Lien"</li>
              <li>Publie et reçois des votes !</li>
            </ol>
          </div>
        </div>

        <button
          onClick={onClose}
          className="absolute top-3 right-3 rounded-full p-1.5 hover:bg-[#FAF3EA] text-[#6E4B2A]"
          aria-label="Fermer"
        >
          <X className="w-4 h-4" />
        </button>
      </DialogContent>
    </Dialog>
  );
}
